import {
  leftArrowTemplate,
  playlistCardTemplate,
  removeNextArrow,
  removePrevArrow,
  rightArrowTemplate,
} from '../carousel/carousel-templates';
import { initCarousel } from '../carousel/init-carousel';
import { init as initPlayers } from '../video-player/init-players';
import { getLabel } from '../../labels/labelling-service';
import { VIDEO_PLAYER_STATUS } from '../video-player/constants';
import { removeElementDom } from '../../utils/dom-utils';
import { includes } from '../../utils/array-utils';
import { isStickyCarouselTemplateEnable } from '../../utils/aem-utils';
import { stickyCarousel } from '../sticky-carousel/sticky-carousel';
import { getCarouselData } from '../carousel/carousel-data';
import { isMobile } from '../../utils/device-utils';

import {
  ACTIVE_CARD_CLASS_NAME,
  BASE_PLAYLIST_ID,
  CONFIG_VIDEO_PLAYLIST,
  DATA_VIDEO_ID,
  DEFAULT_PER_PAGE_ELEMENTS,
  NEXT_CARD_ACTIVE_CLASS_NAME,
  PLAYER_CAROUSEL_ID,
  PLAYLIST_BASE_URL,
  PROGRESS_BAR_CLASS_NAME,
  PROGRESS_TIMER_INTERVAL,
  VIDEO_CARD_CLASS_NAME,
  VIDEO_CARD_IMAGE_CONTAINER_CLASS_NAME,
  VIDEO_WAIT_TIMEOUT,
  VIDEO_CAROUSEL_LOADER_CLASS_NAME,
  $messageRef,
  $loadingModalRef,
  $playlistPlayerContainer,
  $videoCard,
  $playerContainer,
  $playlistCarouselContainer,
  $playlistContainer,
} from './constants';
import { multipleVideoPlaylistState } from './state';

const getVideoId = (slideIndex) => {
  return $videoCard[slideIndex].dataset.videoid;
};

const getProgressBar = (activeCard) => activeCard.getElementsByClassName(PROGRESS_BAR_CLASS_NAME)[0];

export const hideVideoPlayer = () => {
  $playlistPlayerContainer.style.display = 'none';
};

export const showVideoPlayer = () => {
  $playlistPlayerContainer.style.display = 'block';
};

export const removePlaylistContainer = () => {
  const containerClasses = $playlistContainer.classList;
  const editmode = includes('j-editmode')(containerClasses);

  if (!editmode) {
    const gridContainerRef = includes('l-grid')($playlistContainer.parentNode.parentNode.className)
      ? $playlistContainer.parentNode.parentNode
      : null;
    const playlistContainerHasSiblings = $playlistContainer.nextElementSibling !== null;

    removeElementDom($playlistContainer);
    if (!playlistContainerHasSiblings && gridContainerRef) {
      gridContainerRef.classList.remove('l-offset');
    }
  }
};

const destroyCarousel = (carouselId) => {
  const carousel = document.querySelector(`#${carouselId}`);
  if (carousel) {
    if ($playlistCarouselContainer.hasChildNodes()) {
      $playlistCarouselContainer.removeChild(carousel);
    }
  }
};

const createNewCarouselElement = (carouselId) => {
  const carousel = document.createElement('div');
  carousel.setAttribute('id', carouselId);
  carousel.setAttribute('class', carouselId);
  $playlistCarouselContainer.appendChild(carousel);
};

const resetPlayerListeners = (
  player,
  events = [VIDEO_PLAYER_STATUS.READY, VIDEO_PLAYER_STATUS.COMPLETE, VIDEO_PLAYER_STATUS.REPLAY_CLICK]
) => {
  for (const event of events) {
    player.off(event);
  }
};

const getIndexFromAssetId = (elementIds, assetId) => {
  return elementIds.findIndex((element) => element === assetId);
};

export const computePerPageElementsByCarouselWidth = () => {
  if ($playerContainer) {
    const widgetWidth = $playerContainer.clientWidth;
    const perPage = {};
    if (widgetWidth >= 800) {
      perPage[800] = 4.5; //  4 + 1/2  items for carousel width wider than 800px
    } else if (widgetWidth >= 600) {
      perPage[600] = 3; // 3 + 1/2  items for carousel width wider than 300px
    } else {
      perPage[300] = 2.5;
    }
    return perPage;
  }
  return DEFAULT_PER_PAGE_ELEMENTS;
};

const filterVideoCarouselPlayer = (players) => {
  const { playerList, playerConfigList } = players;
  const playerWithCarouselIndex = playerConfigList.findIndex((player) => player.containerid === PLAYER_CAROUSEL_ID);
  return playerList[playerWithCarouselIndex];
};

const playVideo = (videoId) => {
  $playerContainer.setAttribute(DATA_VIDEO_ID, videoId);
  const players = initPlayers();
  return filterVideoCarouselPlayer(players);
};

const playNextVideoIndex = (videindex) => {
  const videoId = getVideoId(videindex);
  playVideo(videoId);
};

const onVideoReady = (videoIndex) => {
  const activeCard = $videoCard[videoIndex];
  activeCard.className = ACTIVE_CARD_CLASS_NAME;
};

const animateComponent = (element) => {
  const progressBarIncrement = 100 / (VIDEO_WAIT_TIMEOUT / PROGRESS_TIMER_INTERVAL);
  element.value += progressBarIncrement;
};

const cleanActiveCard = (activeCardIndex) => {
  const activeCard = $videoCard[activeCardIndex];
  const videoCardImageContainer = activeCard.getElementsByClassName(VIDEO_CARD_IMAGE_CONTAINER_CLASS_NAME)[0];
  const activeCardProgressBar = getProgressBar(activeCard);
  activeCard.className = VIDEO_CARD_CLASS_NAME;
  videoCardImageContainer.removeAttribute('data-next-video');
  activeCardProgressBar.value = 0;
  activeCardProgressBar.style.display = 'none';
};

const setActiveCard = (cardNavigator) => {
  const card = $videoCard[cardNavigator];
  const videoCardImageContainer = card.getElementsByClassName(VIDEO_CARD_IMAGE_CONTAINER_CLASS_NAME)[0];
  const progressBar = getProgressBar(card);
  card.className = NEXT_CARD_ACTIVE_CLASS_NAME;
  videoCardImageContainer.setAttribute('data-next-video', getLabel('video.playlist.next.video'));
  progressBar.style.display = 'block';
  return progressBar;
};

const setActiveCardOnReplay = (cardNavigator) => {
  const card = $videoCard[cardNavigator];
  card.className = ACTIVE_CARD_CLASS_NAME;
};

const setPlayerInAutoplay = (playerid, isAutostart) => {
  const currentPlayerContainer = document.querySelector(`#${playerid}`);
  if (currentPlayerContainer.dataset.autostart !== isAutostart) {
    currentPlayerContainer.dataset.autostart = isAutostart;
  }
};

const onVideoComplete = (nextVideoIndex, carousel) => {
  setPlayerInAutoplay(PLAYER_CAROUSEL_ID, true);
  cleanActiveCard(nextVideoIndex - 1);
  const progressBar = setActiveCard(nextVideoIndex);
  const progressTimerIntervalId = setInterval(animateComponent, PROGRESS_TIMER_INTERVAL, progressBar);
  const videoWaitTimeoutId = setTimeout(() => {
    playNextVideoIndex(nextVideoIndex);
    carousel.jumpToSlide(nextVideoIndex);
    progressBar.style.display = 'none';
    clearInterval(progressTimerIntervalId);
  }, VIDEO_WAIT_TIMEOUT);
  return {
    progressTimerIntervalId,
    videoWaitTimeoutId,
  };
};

const stopVideoCompletedAnimations = (progressTimerIntervalId, videoWaitTimeoutId) => {
  if (progressTimerIntervalId && videoWaitTimeoutId) {
    clearTimeout(videoWaitTimeoutId);
    clearInterval(progressTimerIntervalId);
  }
};

export const preparePlaylistPlayer = () => {
  $playlistPlayerContainer.classList.add('j-player');
};

/**
 * Initialize new Video Playlist
 * @param {Array}  data the model for video carousel playlist
 * @param {String} carouselId The Id to use as a new carouselId.
 */
export const initVideoPlaylist = (data, carouselId) => {
  const carouselData = [...data];
  let currentVideoIndex = 0;
  let carouselEndReached = false;
  let timers = {
    progressTimerIntervalId: undefined,
    videoWaitTimeoutIdId: undefined,
  };
  const dataIds = carouselData.map((element) => element.assetId);

  const onCardClicked = (metadata) => {
    setPlayerInAutoplay(PLAYER_CAROUSEL_ID, true);
    const assetId = metadata.assetId;
    const slideIndex = getIndexFromAssetId(dataIds, assetId);
    if (currentVideoIndex !== slideIndex) {
      slideIndex === dataIds.length - 1 ? (carouselEndReached = true) : (carouselEndReached = false);
      cleanActiveCard(currentVideoIndex);
      // eslint-disable-next-line no-use-before-define
      carousel.jumpToSlide(slideIndex);
      stopVideoCompletedAnimations(timers.progressTimerIntervalId, timers.videoWaitTimeoutId);
      playVideo(metadata.assetId);
      currentVideoIndex = slideIndex;
    } else {
      // TODO: click on the same slide you're playing
    }
  };
  createNewCarouselElement(carouselId);
  CONFIG_VIDEO_PLAYLIST.carouselConfig.perPage = computePerPageElementsByCarouselWidth();

  const carousel = initCarousel(
    carouselId,
    carouselData,
    playlistCardTemplate,
    onCardClicked,
    CONFIG_VIDEO_PLAYLIST.carouselConfig
  );

  const player = playVideo(getVideoId(currentVideoIndex));

  if (isStickyCarouselTemplateEnable()) {
    console.info('[ STICKY - CAROUSEL ] initialize sticky for carousel');
    stickyCarousel(player);
  }

  player.on(VIDEO_PLAYER_STATUS.READY, () => onVideoReady(currentVideoIndex));

  player.on(VIDEO_PLAYER_STATUS.COMPLETE, () => {
    if (currentVideoIndex < dataIds.length - 1) {
      currentVideoIndex++;
      timers = onVideoComplete(currentVideoIndex, carousel);
      carouselEndReached = false;
    } else {
      carouselEndReached = true;
    }
  });

  player.on(VIDEO_PLAYER_STATUS.REPLAY_CLICK, () => {
    if (!carouselEndReached) {
      stopVideoCompletedAnimations(timers.progressTimerIntervalId, timers.videoWaitTimeoutId);
      cleanActiveCard(currentVideoIndex);
      currentVideoIndex--;
      setActiveCardOnReplay(currentVideoIndex);
    }
  });

  return {
    destroy: (carouselIdToDestroy) => {
      stopVideoCompletedAnimations(timers.progressTimerIntervalId, timers.videoWaitTimeoutId);
      resetPlayerListeners(player);
      destroyCarousel(carouselIdToDestroy);
      carousel.destroy();
    },
    pause: () => player.pause(),
    play: () => player.play(),
  };
};

/**
 * Dynamic build new Carousel Id
 * @param {number} tabIndex the index of the tab
 */
export const buildCarouselId = (tabIndex) => BASE_PLAYLIST_ID + tabIndex;

/**
 * Get ALL Carousel Data and collect just the good ones into an array
 * If the array is empty throw an error
 * @param {array} initialTabsData tabs-data useful to fetch the playlists
 * @returns {array} contains all the successful tabs-data responses. The elements are objects with "carouselData", "tabData" and "error" keys
 */
export const getAllCarouselData = async ({ initialTabsData, hlPrevTags = false }) => {
  const arrayOfPromises = initialTabsData.map((tabData) => {
    return getCarouselData(PLAYLIST_BASE_URL, tabData, hlPrevTags)
      .then((carouselData) => {
        return { carouselData, tabData, error: false };
      })
      .catch((error) => {
        console.warn(
          `[ INIT - CAROUSEL ] multiple-video-playlist error in getCarouselData() on tabIndex: ${tabData.tabIndex}`
        );
        return { carouselData: [], tabData, error };
      });
  });
  const allCarouselData = await Promise.all(arrayOfPromises);
  const successCarouselData = allCarouselData.filter((data) => !data.error && data.carouselData.length);
  if (!successCarouselData.length && hlPrevTags) {
    throw new Error('Error in Carousel Data: even hlPrevTags failed');
  }
  return successCarouselData;
};

/**
 * Show / Hide Loading component
 * @param {boolean} isLoading if true show loading component otherwise hide component
 */
export const isCarouselLoading = (isLoading = true) => {
  const display = isLoading ? 'flex' : 'none';
  if ($loadingModalRef) {
    $loadingModalRef.style.display = display;
    document.querySelector(`#${VIDEO_CAROUSEL_LOADER_CLASS_NAME}`).style.display = isLoading ? 'block' : 'none';
    $messageRef.innerHTML = '';
  }
};

/**
 * Clear error
 */
export const clearError = () => {
  if ($loadingModalRef) {
    $loadingModalRef.style.display = 'none';
    $messageRef.innerHTML = '';
  }
  showVideoPlayer();
};

/**
 * Render the carousel arrows
 * @param {number} dataLength length of the carousel data
 */
export const renderArrows = (dataLength) => {
  if (!isMobile()) {
    if (dataLength > CONFIG_VIDEO_PLAYLIST.renderArrowsAfter) {
      rightArrowTemplate();
      leftArrowTemplate();
    } else {
      removePrevArrow();
      removeNextArrow();
    }
  }
};

/**
 * Set new playlist to the video carousel
 * @param {object} data the data carousel to set
 * @param {object} playlist the current playlist
 * @param {string} carouselId new carousel Id
 */
export const setPlaylist = (data, playlistToSet, carouselId) => {
  if (
    multipleVideoPlaylistState.getState('selectedTabId') === carouselId ||
    !multipleVideoPlaylistState.getState('selectedTabId')
  ) {
    if (playlistToSet !== undefined) {
      playlistToSet.destroy(multipleVideoPlaylistState.getState('currentCarouselId'));
    }
    multipleVideoPlaylistState.setState('currentCarouselId', carouselId);
    clearError();
    return initVideoPlaylist(data, carouselId);
  }
};

/**
 * Render the error component and remove navigation arrows
 * @param {object} playlist playlist to destroy
 * @param {string} playlistId current playlistId
 */
export const renderError = (playlistToSet, playlistId) => {
  console.warn(`[ INIT - CAROUSEL ] renderError playlist:${playlistToSet} playlistId:${playlistId}`);
  if (multipleVideoPlaylistState.getState('selectedTabId') === playlistId || playlistId === undefined) {
    hideVideoPlayer();
    if ($loadingModalRef) {
      $loadingModalRef.style.display = 'flex';
      $messageRef.innerHTML = getLabel('video.playlist.error');
      document.querySelector(`#${VIDEO_CAROUSEL_LOADER_CLASS_NAME}`).style.display = 'none';
    }
    if (playlistToSet) {
      playlistToSet.pause();
      playlistToSet.destroy(multipleVideoPlaylistState.getState('currentCarouselId'));
    }
    removePrevArrow();
    removeNextArrow();
  }
};

/**
 * Tabs click callback
 * @param {*} element tab element clicked
 */
export const tabsClickCallback = async (element) => {
  const elementData = JSON.parse(element.dataset.json);
  const carouselId = buildCarouselId(elementData.tabIndex);
  try {
    if (carouselId !== multipleVideoPlaylistState.getState('selectedTabId')) {
      multipleVideoPlaylistState.setState('selectedTabId', carouselId);
      if (multipleVideoPlaylistState.getState('tabsMap').has(carouselId)) {
        clearError();
        const tabData = multipleVideoPlaylistState.getState('tabsMap').get(carouselId);
        renderArrows(tabData.length);
        multipleVideoPlaylistState.setState(
          'playlist',
          setPlaylist(tabData, multipleVideoPlaylistState.getState('playlist'), carouselId)
        );
      } else {
        isCarouselLoading();
        const tabCarouselData = await getCarouselData(PLAYLIST_BASE_URL, elementData);
        isCarouselLoading(false);
        if (tabCarouselData.length > 0) {
          renderArrows(tabCarouselData.length);
          const updatedTabsMap = multipleVideoPlaylistState.getState('tabsMap').set(carouselId, tabCarouselData);
          multipleVideoPlaylistState.setState('tabsMap', updatedTabsMap);
          multipleVideoPlaylistState.setState(
            'playlist',
            setPlaylist(tabCarouselData, multipleVideoPlaylistState.getState('playlist'), carouselId)
          );
        } else {
          renderError(multipleVideoPlaylistState.getState('playlist'), carouselId);
        }
      }
    }
  } catch (error) {
    renderError(multipleVideoPlaylistState.getState('playlist'), carouselId);
  } finally {
    multipleVideoPlaylistState.setState('selectedTabId', carouselId);
  }
};
